import type { CrashlyticsPort } from "@yjsgames/crashlytics-ports";

/**
 * 전역 JS 에러 → Crashlytics 비치명 기록 브리지. Phaser 게임은 네이티브 크래시보다 JS 런타임 에러가 실질
 * 신호라 window의 uncaught error + unhandledrejection을 `recordException`으로 전송한다(웹은 Noop port라 무해).
 *
 * `logCollector`(문의 메일용 링버퍼)·`installConsoleCapture`(dev 파일)와 독립 공존 — 각자 자기 채널로만 보낸다.
 *
 * @param target 리스너를 붙일 이벤트 타깃 — 기본 `window`. 주입형이라 jsdom/fake 타깃으로 결선·해제 테스트
 *   가능(`installConsoleCapture`·`installGlobalErrorTrap`과 동형).
 * @returns 리스너 해제 함수(테스트/정리용). 부팅 1회 설치 후 보통 해제하지 않는다.
 */
export function installCrashlyticsErrorBridge(
  port: CrashlyticsPort,
  target: Pick<Window, "addEventListener" | "removeEventListener"> = window,
): () => void {
  const onError = (event: ErrorEvent): void => {
    const info = extractCrashInfo(event);
    port.recordException(info.message, info.stacktrace ? { stacktrace: info.stacktrace } : undefined);
  };
  const onRejection = (event: PromiseRejectionEvent): void => {
    const info = extractCrashInfo(event);
    port.recordException(info.message, info.stacktrace ? { stacktrace: info.stacktrace } : undefined);
  };
  target.addEventListener("error", onError as EventListener);
  target.addEventListener("unhandledrejection", onRejection as EventListener);
  return () => {
    target.removeEventListener("error", onError as EventListener);
    target.removeEventListener("unhandledrejection", onRejection as EventListener);
  };
}

/** 브리지가 전송할 크래시 정보 — 순수 추출 결과(테스트 대상). */
export interface CrashInfo {
  readonly message: string;
  readonly stacktrace?: { fileName?: string; lineNumber?: number }[];
}

/**
 * ErrorEvent/PromiseRejectionEvent에서 메시지·스택을 추출하는 순수 함수(window 무의존, 단위테스트 가능).
 * Error 객체면 `name: message` + (가능 시) 파일/라인 1프레임을, rejection이 비-Error면 String화한다.
 */
export function extractCrashInfo(event: ErrorEvent | PromiseRejectionEvent): CrashInfo {
  const reason = "reason" in event ? event.reason : event.error;
  const prefix = "reason" in event ? "Unhandled rejection" : "Uncaught error";
  if (reason instanceof Error) {
    const message = `${prefix}: ${reason.name}: ${reason.message}`;
    // ErrorEvent는 filename/lineno를 추가 제공 — 유효 파일+라인일 때만 1프레임 첨부(네이티브 deobfuscation
    // 보조). 인라인/eval 오류의 filename="" lineno=0은 의미 없는 프레임이므로 제외.
    if (!("reason" in event) && event.filename && event.lineno > 0) {
      return { message, stacktrace: [{ fileName: event.filename, lineNumber: event.lineno }] };
    }
    return { message };
  }
  // 비-Error 거부(문자열·객체 등) — 메시지 화이트닝 후 String화.
  const text = "message" in event && event.message ? event.message : String(reason);
  return { message: `${prefix}: ${text}` };
}
